import React, { useEffect, useState } from 'react';
import { useForm, useFormContext, UseFormReturn, useFormState, useWatch } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { AnyAction } from 'redux';

import KogAutoCompleteControlled from '@/main/components/shared/inputAutoComplete/autoCompleteControlled';
import Percentage from '@/main/components/shared/percentage/percentage';
import KOGRadio from '@/main/components/shared/radioButton/radioButton';
import SelectXnuupPartIdAutoCompleteComponent from '@/main/components/shared/xnuupPartIdAutoComplete/selectXnuupPartIdAutoCompleteComponent';

import { KeyValue } from '@/main/models/common';
import { PositionRequest } from '@/main/models/request';
import { SubcategoryRequest } from '@/main/models/request/subcategoryRequest';
import {
  MappingPartSuggestionViewResponse,
  QuoteItemMappingPartViewReponse,
  XnuupPartIdAutoCompleteResponse
} from '@/main/models/response';
import { getTranslateByKey, isNullUndefined } from '@/main/services/utils';
import { getCategory, useCategoryReducer } from '@/main/store/category';
import {
  getPosition,
  resetReducerPosition,
  usePositionReducer
} from '@/main/store/position';
import {
  getSubcategory,
  resetReducerSubcategory,
  useSubcategoryReducer
} from '@/main/store/subcategory';

import * as S from './styles';
import { KeyValueId } from '@/main/models/common/keyValueId';
import RenderTooltipMessage from '@/main/components/shared/renderTooltipMessage/renderTooltipMessage';
interface CategoryDropdown extends KeyValue {
  ignoreWatcher?: boolean;
}
const INITIAL_FORM_STATE = {
  guidDetails: '' as string,
  category: null as CategoryDropdown | null,
  subCategory: null as KeyValue | null,
  position: null as KeyValue | null,
  part: null as KeyValueId | null,
  partName: '' as string
};
export type IFormDetailsMappingParts = typeof INITIAL_FORM_STATE;

interface IRowManualMappingPartsProps {
  optionSelected: string;
  handleSelectOption: (option: string, dataOption: MappingPartSuggestionViewResponse | null) => void;
  idUniqueReducer: string | null;
}

function RowManualMappingParts({
  optionSelected,
  handleSelectOption,
  idUniqueReducer
}: IRowManualMappingPartsProps) {
  const KEY_REDUCER = `RowManualMappingParts_${idUniqueReducer}`;
  const dispatch = useDispatch();
  const {
    control,
    clearErrors,
    setValue,
    resetField,
    register,
    getValues
  } = useForm({
    defaultValues: INITIAL_FORM_STATE
  });

  const { errors } = useFormState({ control });
  const watcherCategory = useWatch({
    control: control,
    name: 'category'
  });
  const watcherSubCategory = useWatch({
    control: control,
    name: 'subCategory'
  });
  const watcherPart = useWatch({
    control: control,
    name: 'part'
  });
  const watcherPosition = useWatch({
    control: control,
    name: 'position'
  });
  const { category: categoryReducer } = useCategoryReducer();
  const { isolated: subcategoryReducer } = useSubcategoryReducer(KEY_REDUCER);
  const { isolated: positionReducer } = usePositionReducer(KEY_REDUCER);

  const [hasClearValue, setHasClearValue] = useState(false);

  //#region Core Functions
  function resetReducers(keyFields: (keyof IFormDetailsMappingParts)[] = []) {
    const mapReducerField: {
      [K in keyof IFormDetailsMappingParts]?: (
        keyReducerAction?: string
      ) => AnyAction;
    } = {
      subCategory: resetReducerSubcategory,
      position: resetReducerPosition,
      part: resetReducerPosition
    };
    const keysAllFields = Object.keys(
      mapReducerField
    ) as (keyof IFormDetailsMappingParts)[];
    const fieldsToReset = keyFields.length === 0 ? keysAllFields : keyFields;

    Object.entries(mapReducerField).forEach(([key, resetReducerField]) => {
      if (fieldsToReset.includes(key as keyof IFormDetailsMappingParts)) {
        dispatch(resetReducerField(KEY_REDUCER));
      }
    });
  }
  function resetFields(
    keyFields:
      | (keyof IFormDetailsMappingParts)[]
      | keyof IFormDetailsMappingParts = []
  ) {
    const arrayFields = Array.isArray(keyFields) ? keyFields : [keyFields];
    arrayFields.forEach(key => {
      if (key === 'partName') setHasClearValue(true);
      resetField(key);
    });
    resetReducers(arrayFields);
  }
  //#endregion

  //#region Call API Domains
  function callCategoryApiDomain() {
    resetFields(['category', 'subCategory', 'position', 'part']);
    if (isNullUndefined(categoryReducer.data)) dispatch(getCategory(false));
  }

  function callSubCategoryApiDomain(categorySelected: CategoryDropdown | null) {
    if (isNullUndefined(categorySelected?.ignoreWatcher)) {
      resetFields(['subCategory', 'part', 'position', 'partName']);
    }

    if (isNullUndefined(categorySelected)) return;
    const request: SubcategoryRequest = {
      guid: categorySelected.key as string,
      isCanastaBasica: false
    };
    dispatch(getSubcategory(request, KEY_REDUCER));
  }
  function loadOptionsByXnuupPartId(partId: XnuupPartIdAutoCompleteResponse) {
    if (!partId) return resetField('part');
    setValue('partName', partId.partPIESName);

    const {
      partCategoryGuid,
      partCategoryName,
      partGuid,
      partPIESName,
      partSubCategoryGuid,
      partSubCategoryName,
      piesPartTerminologyId
    } = partId;

    if (partCategoryGuid && partCategoryName) {
      clearErrors();
      setValue('category', {
        key: partCategoryGuid,
        value: partCategoryName,
        ignoreWatcher: true

      });
      if (isNullUndefined(categoryReducer.data)) dispatch(getCategory());

      if (partSubCategoryGuid && partSubCategoryName) {
        setValue('subCategory', {
          key: partSubCategoryGuid,
          value: partSubCategoryName
        });
        const request: SubcategoryRequest = {
          guid: String(partCategoryGuid),
          isCanastaBasica: false
        };
        dispatch(getSubcategory(request, KEY_REDUCER));

        if (partGuid) {
          const request: PositionRequest = {
            partGuid: String(partGuid)
          };
          dispatch(getPosition(request, KEY_REDUCER));
        } else {
          dispatch(resetReducerPosition(KEY_REDUCER));
          setValue('position', null);
        }
      } else {
        dispatch(resetReducerSubcategory(KEY_REDUCER));
        setValue('subCategory', null);
      }
    } else {
      setValue('category', null);
    }

    setValue('part', {
      id: piesPartTerminologyId,
      key: partGuid,
      value: partPIESName
    });
  }
  //#endregion


  function customHandleSelectOption(option: string) {
    const valuesManual = getValues();
    const dataOption: MappingPartSuggestionViewResponse = {
      confidence: 0,
      guidPartCategory: isNullUndefined(valuesManual.category) ? '' : String(valuesManual.category.key),
      guidPartPosition: isNullUndefined(valuesManual.position) ? '' : String(valuesManual.position.key),
      guidPartSubCategory: isNullUndefined(valuesManual.subCategory) ? '' : String(valuesManual.subCategory.key),
      piesPartGuid: isNullUndefined(valuesManual.part) ? '' : String(valuesManual.part.key),
      piesPartId: isNullUndefined(valuesManual.part) ? 0 : Number(valuesManual.part.id),
      partCategoryName: isNullUndefined(valuesManual.category) ? '' : String(valuesManual.category.value),
      partPositionName: isNullUndefined(valuesManual.position) ? '' : String(valuesManual.position.value),
      partSubCategoryName: isNullUndefined(valuesManual.subCategory) ? '' : String(valuesManual.subCategory.value),
      order: 'manual',
      xnuupPartId: isNullUndefined(valuesManual.part) ? '' : String(valuesManual.part.value),
      piesPosId: isNullUndefined(valuesManual.position) ? 0 : Number(valuesManual.position.key),
    }
    handleSelectOption(option, dataOption);
  }
  useEffect(() => {
    callCategoryApiDomain();
    return () => {
      resetReducers();
    };
  }, []);

  useEffect(() => {
    callSubCategoryApiDomain(watcherCategory);
  }, [watcherCategory?.key]);
  useEffect(() => {
    clearErrors(['category', 'subCategory', 'part', 'position']);
  }, [optionSelected]);

  const percentage = 0;
  const disabledCategoryReducer = isNullUndefined(categoryReducer.data);
  const disabledSubCategoryReducer = isNullUndefined(subcategoryReducer.data);
  const disabledPositionReducer = isNullUndefined(positionReducer.data);
  const isChecked = optionSelected === 'manual';
  const allFieldsFilled = !isNullUndefined(watcherCategory) && !isNullUndefined(watcherSubCategory) && !isNullUndefined(watcherPart) && !isNullUndefined(watcherPosition);
  const messageTooltipKogRadio = allFieldsFilled ? '' : getTranslateByKey("information.fillAllFielToEnableOption");
  return (
    <S.ContainerRow $notClickable>
      <RenderTooltipMessage tooltipMessage={messageTooltipKogRadio}>
        <KOGRadio
          id={`id-mappingParts-radio-manual`}
          value={'manual'}
          checked={isChecked}
          disabled={!allFieldsFilled}
          onChange={(_, checked) => customHandleSelectOption(checked ? 'manual' : '')}
        />
      </RenderTooltipMessage>
      <KogAutoCompleteControlled
        id="id-mappingParts-select-category"
        nameCustom="category"
        label={getTranslateByKey('quoteCard.category')}
        isDefaultValueUnique
        control={control}
        setValueCustom={setValue}
        options={categoryReducer.data}
        isLoading={categoryReducer.isLoading}
        disabled={disabledCategoryReducer}
        rulesCustom={{ required: true }}
        onChangeCustom={optionSelected => { }}
      />
      <KogAutoCompleteControlled
        id="id-mappingParts-select-subCategory"
        nameCustom="subCategory"
        label={getTranslateByKey('quoteCard.subCategory')}
        isDefaultValueUnique
        control={control}
        setValueCustom={setValue}
        options={subcategoryReducer.data}
        isLoading={subcategoryReducer.isLoading}
        disabled={disabledSubCategoryReducer}
        rulesCustom={{ required: true }}
        onChangeCustom={() => { }}
      />
      <SelectXnuupPartIdAutoCompleteComponent
        isCanastaBasica={false}
        showIdPIES={true}
        required
        partPIESName={watcherPart?.value}
        watcherCategory={watcherCategory}
        watcherSubcategory={watcherSubCategory}
        handleChangeXnuupPartId={partId => {
          loadOptionsByXnuupPartId(partId);
        }}
        errorMessageCustom={
          errors.part?.type === 'required'
            ? getTranslateByKey('information.xnuupPartIdRequired', true)
            : ''
        }
        hasClearValue={hasClearValue}
        setHasClearValue={setHasClearValue}
        hasErrorCustom={!!errors.part}
        label={getTranslateByKey('information.part')}
        {...register('part', { required: true })}
      />
      <KogAutoCompleteControlled
        id="id-mappingParts-select-position"
        nameCustom="position"
        label={getTranslateByKey('information.position')}
        isDefaultValueUnique
        control={control}
        setValueCustom={setValue}
        options={positionReducer.data}
        isLoading={positionReducer.isLoading}
        disabled={disabledPositionReducer}
        rulesCustom={{ required: true }}
        onChangeCustom={() => { }}
      />
      <S.ContainerLabel
        $labelContent={getTranslateByKey('information.reliabilityPercentage')}>
        <Percentage percentage={percentage} />
      </S.ContainerLabel>
    </S.ContainerRow>
  );
}

export default RowManualMappingParts;
